import React, { useState, forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import CustomInput from 'components/shared/forms/CustomInput';
import CountrySelect from 'components/shared/CountrySelect/CountrySelect';
import DropdownSelect, { ISelectOption } from 'components/shared/forms/DropdownSelect/DropdownSelect';
import styles from './CustomerBillingInfoForm.module.scss';
import iso3166 from "iso-3166-2";
import { IAddress } from 'features/billing/customer/customerTypes';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'app/store';
import { getStripeTaxIdTypesByCountry } from 'helpers/getStripeTaxIdTypesByCountry';
import { COUNTRIES_REQUIRING_STATE_OR_PROVINCE_FOR_TAX, getCountryCurrency } from 'helpers/getCountryCurrency';
import { billingFormsCustomerInfoSelector } from 'features/billing/formStates/billingFormsInfo/billingFormsInfoSlice';
import { setCustomerFormInfo, setCustomerFormInfoAddress, setCustomerFormInfoAddressErrors, setCustomerFormInfoTaxIds, setSubscriptionFormInfoCurrency } from 'features/billing/formStates/billingFormsInfo/billingFormsInfoActions';

export interface CustomerBillingInfoFormHandle {
  triggerSubmit: () => { address: IAddress; taxIds: Record<string, string> } | null;
}

const CustomerBillingInfoForm = forwardRef<CustomerBillingInfoFormHandle>((_, ref) => {
  const dispatch = useAppDispatch();

  const customerFormInfo = useSelector(billingFormsCustomerInfoSelector);
  const { taxIds, taxIdErrors, address, addressErrors } = customerFormInfo;

  const companyCustomerType = "Company / Organization";
  const individualCustomerType = "Individual";

  const customerTypeOptions: ISelectOption[] = [
    { value: individualCustomerType, label: individualCustomerType },
    { value: companyCustomerType, label: companyCustomerType },
  ];

  const [customerType, setCustomerType] = useState(Object.keys(taxIds).length > 0 ? companyCustomerType : individualCustomerType);

  const getStateOptions = (countryCode: string): ISelectOption[] => {
    if (!COUNTRIES_REQUIRING_STATE_OR_PROVINCE_FOR_TAX.includes(countryCode.toLocaleUpperCase())) {
      return [];
    }

    const countryData = iso3166.data[countryCode];
    if (!countryData?.sub) return [];

    return Object.entries(countryData.sub).map(([code, name]) => ({
      value: code,
      label: name.name
    }));
  };

  const stateOptions = getStateOptions(address.country);

  const handleInputChange = (field: keyof IAddress, value: string) => {
    dispatch(setCustomerFormInfoAddress({ ...address, [field]: value }));
  };

  const handleEntityTypeChange = (selectedOption: ISelectOption | null) => {
    if (selectedOption) {
      setCustomerType(selectedOption.value);

      if (selectedOption.value === individualCustomerType && (Object.keys(taxIds).length > 0 ||  Object.keys(taxIdErrors).length > 0)) {
        dispatch(setCustomerFormInfo({
          ...customerFormInfo,
          taxIds: {},
          taxIdErrors: {},
        }))
      }
    }
  };

  const handleCountryChange = (countryCode: string) => {
    getCountryCurrency(countryCode)
      .then((currency) => {
        dispatch(setSubscriptionFormInfoCurrency(currency))
      });

    dispatch(setCustomerFormInfo({
      ...customerFormInfo,
      taxIds: {},
      taxIdErrors: {},
      address: {
        ...address,
        country: countryCode,
        state: getStateOptions(countryCode)[0]?.value ?? ''
      }
    }))
  };

  const handleTaxIdChange = (taxType: string, taxId: string) => {
    const newTaxIds = { ...taxIds };
    taxId = taxId.trim();

    if (taxId) {
      newTaxIds[taxType] = taxId;
    } else {
      delete newTaxIds[taxType];
    }

    dispatch(setCustomerFormInfoTaxIds(newTaxIds));
  };

  useImperativeHandle(ref, () => ({
    triggerSubmit: () => {
      let hasError = false;
      const requiredFields: (keyof IAddress)[] = ['country', 'line1', 'postalCode', 'city'];
      const newErrors: Partial<Record<keyof IAddress, string>> = {};

      requiredFields.forEach((field) => {
        if (address[field].trim() === '') {
          newErrors[field] = 'This field is required';
          hasError = true;
        }
        else {
          newErrors[field] = '';
        }
      });

      dispatch(setCustomerFormInfoAddressErrors(newErrors as Omit<IAddress, "line2">))

      if (hasError) return null;
      return { address, taxIds };
    }
  }));

  return (
    <form className={styles.customerInfoForm} onSubmit={(e) => e.preventDefault()}>
      <DropdownSelect
        label="Select customer type"
        options={customerTypeOptions}
        value={customerTypeOptions.find((option) => option.value === customerType)}
        onChange={(option) => handleEntityTypeChange(option as ISelectOption)}
      />

      {customerType === companyCustomerType && (
        getStripeTaxIdTypesByCountry(address.country, address.state).map((taxInfo) => (
          <React.Fragment key={taxInfo.type}>
            <CustomInput
              type="text"
              label={taxInfo.name}
              placeholder={taxInfo.example}
              value={taxIds[taxInfo.type] || ''}
              onChange={(e) => handleTaxIdChange(taxInfo.type, e.target.value)}
            />
            {taxIdErrors[taxInfo.type] && (
              <p className={styles.fieldError}>
                {taxIdErrors[taxInfo.type]}
              </p>
            )}
          </React.Fragment>
        ))
      )}

      <CountrySelect
        defaultCountryCode={address.country}
        onCountryChange={handleCountryChange}
      />
      {addressErrors.country && (
        <p className={styles.fieldError}>
          {addressErrors.country}
        </p>
      )}
      {!!stateOptions.length &&
        <DropdownSelect
          label="Select state or province"
          value={stateOptions.find(o => o.value === address.state) ?? stateOptions[0]}
          options={stateOptions}
          onChange={option => {
            if (option) {
              dispatch(setCustomerFormInfoAddress({ ...address, state: option.value }))
            }
          }}
          isSearchable={true}
          blurInputOnSelect
          withDescription
          placeholder="Select country"
        />
      }
      <CustomInput
        type="text"
        label="Address line 1"
        value={address.line1}
        onChange={(e) => handleInputChange('line1', e.target.value)}
      />
      {addressErrors.line1 && (
        <p className={styles.fieldError}>
          {addressErrors.line1}
        </p>
      )}
      <CustomInput
        type="text"
        label="Address line 2"
        value={address.line2}
        onChange={(e) => handleInputChange('line2', e.target.value)}
      />
      <CustomInput
        type="text"
        label="Postal code"
        value={address.postalCode}
        onChange={(e) => handleInputChange('postalCode', e.target.value)}
      />
      {addressErrors.postalCode && (
        <p className={styles.fieldError}>
          {addressErrors.postalCode}
        </p>
      )}
      <CustomInput
        type="text"
        label="City"
        value={address.city}
        onChange={(e) => handleInputChange('city', e.target.value)}
      />
      {addressErrors.city && (
        <p className={styles.fieldError}>
          {addressErrors.city}
        </p>
      )}
    </form>
  );
});

export default CustomerBillingInfoForm;
